import React from "react";
import { cn } from "@/lib/utils";
import { useT } from "@/i18n/LanguageProvider";

export function Field({ label, unit, hint, error, children, testid }) {
  return (
    <div className="space-y-2" data-testid={testid ? `${testid}-field` : undefined}>
      <label className="text-[11px] tracking-[0.08em] uppercase font-semibold text-stone-500 block">
        {label}
      </label>
      <div className="relative">
        {children}
        {unit && (
          <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs font-mono text-stone-500 pointer-events-none">
            {unit}
          </span>
        )}
      </div>
      {hint && <p className="text-xs text-stone-500">{hint}</p>}
      {error && <p className="text-xs text-red-800">{error}</p>}
    </div>
  );
}

export function NumberInput({ value, onChange, placeholder, min, max, step, testid, padRight = true }) {
  return (
    <input
      type="number"
      inputMode="decimal"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      min={min}
      max={max}
      step={step ?? "any"}
      data-testid={testid}
      className={cn(
        "w-full bg-white border border-stone-200 rounded-sm",
        "px-3 py-3 text-base font-mono tabular-nums min-h-[44px]",
        "transition-colors duration-100",
        "hover:border-stone-300 focus:border-sky-700 focus:outline-none focus:ring-2 focus:ring-sky-700/30",
        padRight && "pr-14"
      )}
    />
  );
}

export function Result({ label, value, unit, big = false, testid, accent = "default" }) {
  const valueColor =
    accent === "danger"
      ? "text-red-800"
      : accent === "success"
      ? "text-green-800"
      : "text-stone-900";
  return (
    <div className="border border-stone-200 bg-stone-50 px-5 py-4 rounded-sm" data-testid={testid}>
      <div className="text-[11px] tracking-[0.08em] uppercase font-semibold text-stone-500">
        {label}
      </div>
      <div className="mt-2 flex items-baseline gap-2">
        <span
          className={cn(
            "font-mono tabular-nums tracking-tight",
            big ? "text-4xl md:text-5xl" : "text-2xl md:text-3xl",
            valueColor
          )}
          data-testid={testid ? `${testid}-value` : undefined}
        >
          {value}
        </span>
        {unit && <span className="text-sm text-stone-500 font-mono">{unit}</span>}
      </div>
    </div>
  );
}

export function FormulaBox({ children, testid }) {
  return (
    <div
      className="border border-stone-200 bg-white px-4 py-3 rounded-sm font-mono text-xs leading-relaxed text-stone-700 whitespace-pre-wrap"
      data-testid={testid}
    >
      {children}
    </div>
  );
}

export function PageHeader({ title, subtitle, formula }) {
  const t = useT();
  return (
    <header className="border-b border-stone-200 px-6 md:px-10 py-8 bg-white">
      <div className="max-w-3xl">
        <div className="text-[11px] tracking-[0.18em] uppercase font-semibold text-sky-700 mb-2">
          {t("common.calculator")}
        </div>
        <h1 className="font-display text-3xl md:text-4xl tracking-tight font-medium text-stone-900">
          {title}
        </h1>
        {subtitle && <p className="mt-3 text-stone-600 text-sm md:text-base leading-relaxed">{subtitle}</p>}
        {formula && (
          <div className="mt-5 inline-block border-l-2 border-sky-700 bg-stone-50 px-4 py-2 font-mono text-xs text-stone-700">
            {formula}
          </div>
        )}
      </div>
    </header>
  );
}

export function Note({ children, kind = "info" }) {
  const palette =
    kind === "warning"
      ? "border-amber-800/30 bg-amber-50 text-amber-900"
      : kind === "danger"
      ? "border-red-800/30 bg-red-50 text-red-900"
      : "border-stone-200 bg-stone-50 text-stone-700";
  return (
    <div className={cn("border rounded-sm px-4 py-3 text-xs leading-relaxed", palette)}>
      {children}
    </div>
  );
}
